import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MessageSquare, UserRoundCog, UsersRound } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { DashboardApiError, type DashboardStats } from '../types/dashboard.types';
import { useDashboardSummaryQuery } from '../hooks/use-dashboard';

// PRD 01 v1.3 (2026.07.12) cut the summary row from 5 cards to 3 — "'PRO 개발 현황'·'참고자료
// 개발 현황' 카드 제거", reflected in §1.1·§2.1·§2.2·§3.1·§5 ("3개의 요약 카드"). SHCT-831:
// both removed cards were still rendered here, carried over from the pre-v1.3 prototype.

interface BreakdownItem {
  label: string;
  value: string;
  color: string;
}

interface StatCardModel {
  key: string;
  title: string;
  value: string;
  sub?: string;
  icon: LucideIcon;
  bg: string;
  color: string;
  breakdown: BreakdownItem[];
}

const CARD_COUNT = 3;

// Maps the typed summary payload to display cards. Labels resolve from i18n (`t`); only the
// numeric values come from the API — no Korean copy lives in this component.
function buildCards(stats: DashboardStats, t: TFunction<'admin'>): StatCardModel[] {
  return [
    {
      key: 'patients',
      title: t('dashboard.cards.patients'),
      value: t('dashboard.values.person', { count: stats.totalPatients.total }),
      icon: UsersRound,
      bg: '#E6EEFF',
      color: '#0B2E73',
      // ADM.027 — the headline splits into the three patient states. Same shape and the same
      // var(--chart-N) tokens as the staff card below, so the two cards read as siblings.
      breakdown: [
        {
          label: t('dashboard.breakdown.patientActive'),
          value: String(stats.totalPatients.active),
          color: 'var(--chart-2)',
        },
        {
          label: t('dashboard.breakdown.patientClosed'),
          value: String(stats.totalPatients.closed),
          color: 'var(--chart-3)',
        },
        {
          label: t('dashboard.breakdown.patientWithdrawn'),
          value: String(stats.totalPatients.withdrawn),
          color: 'var(--chart-4)',
        },
      ],
    },
    {
      key: 'staff',
      title: t('dashboard.cards.staff'),
      value: t('dashboard.values.person', { count: stats.totalStaff.total }),
      icon: UserRoundCog,
      bg: '#E6F9F0',
      color: '#33B27D',
      breakdown: [
        { label: t('dashboard.breakdown.doctor'), value: String(stats.totalStaff.doctor), color: 'var(--chart-2)' },
        { label: t('dashboard.breakdown.nurse'), value: String(stats.totalStaff.nurse), color: 'var(--chart-3)' },
        { label: t('dashboard.breakdown.crc'), value: String(stats.totalStaff.crc), color: 'var(--chart-4)' },
      ],
    },
    {
      key: 'inquiries',
      title: t('dashboard.cards.inquiries'),
      value: t('dashboard.values.case', { count: stats.inquiries.today }),
      sub: t('dashboard.values.caseSub', { count: stats.inquiries.cumulative }),
      icon: MessageSquare,
      bg: '#E6EEFF',
      color: '#188AF5',
      breakdown: [
        {
          label: t('dashboard.breakdown.patientInquiry'),
          value: t('dashboard.values.breakdownCase', {
            today: stats.inquiries.today,
            cumulative: stats.inquiries.cumulative,
          }),
          color: 'var(--chart-1)',
        },
      ],
    },
  ];
}

export function DashboardStatsGrid() {
  const { t } = useTranslation('admin');
  const { data, isLoading, isError, error, refetch } = useDashboardSummaryQuery();
  const networkToasted = useRef(false);

  // PRD 01 §8.2: network failure → toast (the card area still shows the inline retry block).
  useEffect(() => {
    if (isError && error instanceof DashboardApiError && error.code === 'NETWORK' && !networkToasted.current) {
      networkToasted.current = true;
      toast.error(t('dashboard.network'));
    }
    if (!isError) networkToasted.current = false;
  }, [isError, error, t]);

  if (isLoading) {
    return (
      <div className="dashboard__stats-grid">
        {Array.from({ length: CARD_COUNT }).map((_, i) => (
          <div className="stat-card stat-card--skeleton" key={i} aria-hidden="true">
            <div className="stat-card__header">
              <span className="skeleton-box" style={{ width: 80, height: 14 }} />
              <span className="skeleton-box" style={{ width: 36, height: 36, borderRadius: 8 }} />
            </div>
            <div className="skeleton-box" style={{ width: 100, height: 28, margin: '4px 0 12px' }} />
            <div className="skeleton-box" style={{ width: '70%', height: 12 }} />
          </div>
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="dashboard__stats-grid">
        <div className="dashboard__error" role="alert">
          <span>{t('dashboard.error.message')}</span>
          <button type="button" className="dashboard__retry-btn" onClick={() => void refetch()}>
            {t('dashboard.error.retry')}
          </button>
        </div>
      </div>
    );
  }

  const cards = buildCards(data, t);

  return (
    <div className="dashboard__stats-grid">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div className="stat-card" key={card.key}>
            <div className="stat-card__header">
              <span className="stat-card__title">{card.title}</span>
              <div className="stat-card__icon" style={{ background: card.bg, color: card.color }}>
                <Icon size={18} />
              </div>
            </div>
            <div className="stat-card__value">
              {card.value}
              {card.sub ? <small>{card.sub}</small> : null}
            </div>
            <div className="stat-card__breakdown">
              {card.breakdown.map((item) => (
                <span className="stat-card__breakdown-item" key={item.label}>
                  <span className="stat-card__dot" style={{ background: item.color }} />
                  {item.label} {item.value}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
